// pages/UserTaskSummaryPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody,
  ListSubheader, Checkbox, ListItemText, MenuItem, Button, Pagination,
  LinearProgress, Chip, IconButton, Tooltip, FormControl,
  Autocomplete, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";

// Firestore + auth
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";

/* ================= helpers ================= */
const norm = (s) => (s ?? "").toString().trim().toLowerCase();
const idNorm = (s) => norm(s).replace(/[^a-z0-9]/g, "");

const fmtDate = (v) => {
  if (!v) return "—";
  const d = v?.toDate ? v.toDate() : new Date(v);
  if (Number.isNaN(d?.getTime?.() ?? d)) return "—";
  return d.toLocaleDateString("en-GB"); // dd/mm/yyyy
};

const parseDMY = (str) => {
  if (!str) return null;
  const [dd, mm, yyyy] = String(str).split(/[-/]/);
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(d.getTime()) ? null : d;
};

const clampPct = (n) => Math.max(0, Math.min(100, Number(n ?? 0)));

const CATEGORY_OPTIONS = [
  "Leadership (owners, managing partners)",
  "Engagement Heads (Experienced Professionals)",
  "Qualified Professionals (CA, CS, ACCA or Other Post Graduate qualifications)",
  "Semi-Qualified Staff (pursuing professional course)",
  "Trainee’s (Interns etc)",
  "Support Staff (other employees)",
  "HR (internal administrative staff)",
];

const chipColorForStageDMY = (stage, dueDMY) => {
  const s = (stage || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "in progress") return "warning";
  const d = parseDMY(dueDMY);
  if (!d) return "default";
  return d < new Date() ? "error" : "default";
};

/** Small table component */
const DataTable = ({
  rows,
  columns,
  onSort,
  sortField,
  sortOrder,
  startIndex = 0,
  onOpenTasks
}) => (
  <Table
    size="small"
    sx={{
      "& th, & td": { py: 0.5, px: 1 },
      "tbody td": { py: 0.5 },

      // Numeric columns centered (includes NA + Reviewed)
      "& thead th:nth-of-type(6), & thead th:nth-of-type(7), & thead th:nth-of-type(8), & thead th:nth-of-type(9), & thead th:nth-of-type(10), & thead th:nth-of-type(11)": {
        textAlign: "center",
      },
      "& tbody td:nth-of-type(6), & tbody td:nth-of-type(7), & tbody td:nth-of-type(8), & tbody td:nth-of-type(9), & tbody td:nth-of-type(10), & tbody td:nth-of-type(11)": {
        textAlign: "center",
      },
    }}
  >
    <TableHead
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& th": (theme) => ({
          bgcolor: theme.palette.grey[100],
          color: theme.palette.grey[900],
          fontWeight: 700,
        }),
      }}
    >
      <TableRow sx={{ "& > *": { py: 0.5, px: 1 } }}>
        <TableCell>S.No.</TableCell>
        {columns.map((col) => {
          const label =
            col === "projectName" ? "Project Name" :
            col === "dueDate" ? "Due Date" :
            col === "completion" ? "Task Completion" :
            col === "stage" ? "Task Stage" :
            col === "tasks" ? "Tasks" :
            col === "totalTasks" ? "Total Tasks" :
            col === "NA" ? "NA" :
            col === "Reviewed" ? "Reviewed" :
            col.charAt(0).toUpperCase() + col.slice(1);

          const arrow = sortField === col ? (sortOrder === "asc" ? " ↑" : " ↓") : "";
          return (
            <TableCell
              key={col}
              onClick={() => onSort?.(col)}
              sx={{ cursor: onSort ? "pointer" : "default" }}
            >
              {label}{arrow}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>

    <TableBody>
      {rows.map((row, idx) => (
        <TableRow
          key={`${row.entity}|${row.user}|${row.projectName}`}
          sx={{ "& > *": { py: 0.5, px: 1 } }}
        >
          <TableCell>{startIndex + idx + 1}</TableCell>

          {columns.map((col) => {
            if (col === "tasks") {
              return (
                <TableCell key={col} align="center">
                  <Tooltip title="Open Tasks">
                    <IconButton size="small" onClick={() => onOpenTasks?.(row)}>
                      <GridViewOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              );
            }

            if (col === "totalTasks") {
              return (
                <TableCell key={col} align="center">
                  <Typography variant="body2">{row.totalTasks ?? 0}</Typography>
                </TableCell>
              );
            }

            if (col === "Pending") {
              const val = Number(row.Pending || 0);
              return (
                <TableCell key={col} align="center">
                  {val > 0 ? <Chip size="small" color="error" label={val} /> : <Typography variant="body2">0</Typography>}
                </TableCell>
              );
            }

            if (col === "In Progress") {
              const val = Number(row["In Progress"] || 0);
              return (
                <TableCell key={col} align="center">
                  {val > 0 ? <Chip size="small" color="warning" label={val} /> : <Typography variant="body2">0</Typography>}
                </TableCell>
              );
            }

            if (col === "Completed") {
              const val = Number(row.Completed || 0);
              return (
                <TableCell key={col} align="center">
                  {val > 0 ? <Chip size="small" color="success" label={val} /> : <Typography variant="body2">0</Typography>}
                </TableCell>
              );
            }

            if (col === "NA") {
              const val = Number(row["Not Applicable"] || 0);
              return (
                <TableCell key={col} align="center">
                  {val > 0 ? <Chip size="small" variant="outlined" label={val} /> : <Typography variant="body2">0</Typography>}
                </TableCell>
              );
            }

            if (col === "Reviewed") {
              const val = Number(row.Reviewed || 0);
              return (
                <TableCell key={col} align="center">
                  {val > 0
                    ? <Chip size="small" variant="outlined" color="success" label={val} />
                    : <Typography variant="body2">0</Typography>}
                </TableCell>
              );
            }

            if (col === "completion") {
              const pct = clampPct(row[col]);
              return (
                <TableCell key={col} align="left" sx={{ minWidth: 200 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress variant="determinate" value={pct} sx={{ height: 10, borderRadius: 5 }} />
                    </Box>
                    <Typography variant="body2">{pct}%</Typography>
                  </Box>
                </TableCell>
              );
            }

            if (col === "stage") {
              const val = row[col] || "Pending";
              return (
                <TableCell key={col}>
                  <Chip size="small" label={val} color={chipColorForStageDMY(val, row?.dueDate)} />
                </TableCell>
              );
            }

            return <TableCell key={col}>{row[col] ?? (col === "dueDate" ? "—" : 0)}</TableCell>;
          })}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default function UserTaskSummaryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyId, user } = useAuth();

  const meUid = user?.uid || "";
  const meNameN = idNorm(user?.name || "");
  const meRole = user?.role || "Member";
  const isAdminOrManager = meRole === "Admin" || meRole === "Manager";

  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  /* -------- NEW: Reviewed filter state -------- */
  // values: "All" | "Reviewed" | "Not Reviewed"
  const [reviewedFilter, setReviewedFilter] = useState("All");

  /* -------- fetch projects + tasks -------- */
  useEffect(() => {
    const run = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const pSnap = await getDocs(query(collection(db, "projects"), where("companyId", "==", companyId)));
        const p = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProjects(p);

        const tSnap = await getDocs(query(collection(db, "tasks"), where("companyId", "==", companyId)));
        setTasks(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [companyId]);

  /* -------- role-aware task visibility -------- */
  const managedProjectNames = useMemo(() => {
    if (meRole !== "Manager") return new Set();
    const s = new Set(
      (projects || [])
        .filter((p) => p?.managerId === meUid || idNorm(p?.managerName) === meNameN)
        .map((p) => (p?.name || "").trim())
        .filter(Boolean)
    );
    return s;
  }, [projects, meRole, meUid, meNameN]);

  const tlProjectNames = useMemo(() => {
    const s = new Set(
      (projects || [])
        .filter((p) => p?.teamLeadId === meUid || idNorm(p?.teamLeadName) === meNameN)
        .map((p) => (p?.name || "").trim())
        .filter(Boolean)
    );
    return s;
  }, [projects, meUid, meNameN]);

  const categoryByProject = useMemo(() => {
    const m = new Map();
    (projects || []).forEach((p) => {
      const name = (p?.name || "").trim();
      if (name) m.set(name, p?.category || "—");
    });
    return m;
  }, [projects]);

  const effectiveTasks = useMemo(() => {
    if (meRole === "Admin") return tasks;

    if (meRole === "Manager") {
      if (!managedProjectNames.size) return [];
      return tasks.filter((t) => managedProjectNames.has((t?.project || "").trim()));
    }

    // Member: my tasks + tasks from TL projects
    const mine = tasks.filter((t) => idNorm(t?.assignedTo) === meNameN);
    const tl = tasks.filter((t) => tlProjectNames.has((t?.project || "").trim()));
    const map = new Map();
    for (const t of mine) map.set(t.id, t);
    for (const t of tl) map.set(t.id, t);
    return Array.from(map.values());
  }, [tasks, meRole, meNameN, managedProjectNames, tlProjectNames]);

  /* -------- company users (for meta filters) -------- */
  const [companyUsers, setCompanyUsers] = useState([]);
  useEffect(() => {
    if (!companyId || !isAdminOrManager) return;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "users"), where("companyId", "==", companyId)));
        const arr = snap.docs.map((d) => {
          const u = d.data() || {};
          const name = u.name || u.displayName || u.email || "Unnamed";
          return {
            id: d.id,
            name,
            isActive: (u.isActive ?? true) === true,
            category: u.category || "",
          };
        });
        arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCompanyUsers(arr);
      } catch (e) {
        console.error("users list failed:", e);
        setCompanyUsers([]);
      }
    })();
  }, [companyId, isAdminOrManager]);

  const userMetaByName = useMemo(() => {
    const m = new Map();
    companyUsers.forEach((u) => m.set(idNorm(u.name), u));
    return m;
  }, [companyUsers]);

  const usersFromVisible = useMemo(() => {
    const s = new Set((effectiveTasks || []).map((t) => (t.assignedTo || "").trim()).filter(Boolean));
    return s;
  }, [effectiveTasks]);

  const usersForFilter = useMemo(() => {
    if (!isAdminOrManager) return [];
    if (meRole === "Admin") return companyUsers;

    const allow = new Set([...usersFromVisible].map((n) => n.toLowerCase()));
    const matched = (companyUsers || []).filter((u) =>
      allow.has((u?.name || "").trim().toLowerCase())
    );
    const missing = [...usersFromVisible]
      .filter((n) => !matched.some((u) => (u?.name || "").trim().toLowerCase() === n.toLowerCase()))
      .map((n) => ({ id: `name:${n}`, name: n }));
    return [...matched, ...missing].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [isAdminOrManager, meRole, companyUsers, usersFromVisible]);

  /* -------- Global meta filters (Admin/Manager only) -------- */
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategories, setFilterCategories] = useState([]);

  const usersForFilterScoped = useMemo(() => {
    if (!isAdminOrManager) return [];
    let arr = usersForFilter;

    if (filterStatus) {
      const wantActive = filterStatus === "Active";
      arr = arr.filter((u) => (u.isActive ?? true) === wantActive);
    }
    if (filterCategories.length) {
      const set = new Set(filterCategories);
      arr = arr.filter((u) => set.has(u.category || ""));
    }
    return [...arr].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [isAdminOrManager, usersForFilter, filterStatus, filterCategories]);

  const categoryOptionsScoped = useMemo(() => {
    if (!isAdminOrManager) return [];
    const set = new Set(
      usersForFilterScoped.map((u) => u.category || "").filter(Boolean)
    );
    return CATEGORY_OPTIONS.filter((c) => set.has(c));
  }, [isAdminOrManager, usersForFilterScoped]);

  useEffect(() => {
    if (!isAdminOrManager) return;
    if (selectedUser && !usersForFilterScoped.some((u) => u.id === selectedUser.id)) {
      setSelectedUser(null);
    }
  }, [isAdminOrManager, usersForFilterScoped, selectedUser]);

  useEffect(() => {
    if (!isAdminOrManager || filterCategories.length === 0) return;
    const allowed = new Set(categoryOptionsScoped);
    const next = filterCategories.filter((c) => allowed.has(c));
    if (next.length !== filterCategories.length) setFilterCategories(next);
  }, [isAdminOrManager, categoryOptionsScoped, filterCategories]);

  /* -------- build summary rows (respecting filters) -------- */
  const summaryRows = useMemo(() => {
    // start from role-scoped tasks
    let base = effectiveTasks;

    // Admin/Manager meta filters
    if (isAdminOrManager) {
      if (selectedUser) {
        const sel = idNorm(selectedUser.name);
        base = base.filter((t) => idNorm(t.assignedTo) === sel);
      }
      if (filterCategories.length || filterStatus) {
        base = base.filter((t) => {
          const meta = userMetaByName.get(idNorm(t.assignedTo));
          if (!meta) return false;
          const okCat = filterCategories.length ? filterCategories.includes(meta.category || "") : true;
          const okStatus = filterStatus ? (filterStatus === "Active" ? meta.isActive : !meta.isActive) : true;
          return okCat && okStatus;
        });
      }
    }

    // ✅ NEW: Reviewed filter (applies for ALL roles)
    if (reviewedFilter === "Reviewed") {
      base = base.filter((t) => Boolean(t?.reviewed));
    } else if (reviewedFilter === "Not Reviewed") {
      base = base.filter((t) => !Boolean(t?.reviewed));
    }

    const bucket = new Map();
    for (const t of base) {
      const entity = t?.entity || "Unknown";
      const userName = t?.assignedTo || "Unassigned";
      const project = t?.project || "Unnamed";
      const key = `${norm(entity)}|${norm(userName)}|${norm(project)}`;

      if (!bucket.has(key)) {
        bucket.set(key, {
          entity,
          user: userName,
          projectName: project,
          totalTasks: 0,
          Pending: 0,
          "In Progress": 0,
          Completed: 0,
          "Not Applicable": 0,
          Reviewed: 0,
          _dueDates: [],
        });
      }

      const row = bucket.get(key);
      row.totalTasks += 1;

      const st = t?.status || "Pending";
      row[st] = (row[st] || 0) + 1;

      if (Boolean(t?.reviewed)) {
        row.Reviewed = (row.Reviewed || 0) + 1;
      }

      if (t?.dueDate) {
        const d = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
        if (!Number.isNaN(d?.getTime?.() ?? d)) row._dueDates.push(d);
      }
    }

    return Array.from(bucket.values()).map((r) => {
      const due =
        r._dueDates.length ? new Date(Math.min(...r._dueDates.map((d) => d.getTime()))) : null;

      const na = Number(r["Not Applicable"] || 0);
      const done = Number(r.Completed || 0) + na;

      const completion = r.totalTasks ? Math.round((done / r.totalTasks) * 100) : 0;
      const stage =
        r.totalTasks === 0 ? "Pending" :
        done === r.totalTasks ? "Completed" :
        r["In Progress"] > 0 ? "In Progress" : "Pending";

      const cat = categoryByProject.get(r.projectName) || "—";
      return { ...r, category: cat, dueDate: fmtDate(due), completion, stage };
    });
  }, [
    effectiveTasks,
    isAdminOrManager,
    selectedUser,
    filterCategories,
    filterStatus,
    userMetaByName,
    categoryByProject,
    reviewedFilter, // ✅ NEW dependency
  ]);

  /* -------- existing table filters -------- */
  const [entitySel, setEntitySel] = useState([]);
  const [userSel, setUserSel] = useState([]);
  const [projectSel, setProjectSel] = useState([]);
  const [categorySel, setCategorySel] = useState([]);

  const [stageSel, setStageSel] = useState([]);
  const [stageQuery, setStageQuery] = useState("");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");

  const [entityQuery, setEntityQuery] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [categoryQuery, setCategoryQuery] = useState("");

  const applySel = (base, keyOf, sel) => {
    if (!sel?.length) return base;
    const allowed = new Set(sel);
    return base.filter((r) => allowed.has(keyOf(r)));
  };

  const entityOptions = useMemo(() => {
    let base = summaryRows;
    base = applySel(base, (r) => r.user, userSel);
    base = applySel(base, (r) => r.projectName, projectSel);
    return Array.from(new Set(base.map((r) => r.entity))).sort((a, b) => a.localeCompare(b));
  }, [summaryRows, userSel, projectSel]);

  const projectOptions = useMemo(() => {
    let base = summaryRows;
    base = applySel(base, (r) => r.entity, entitySel);
    base = applySel(base, (r) => r.user, userSel);
    return Array.from(new Set(base.map((r) => r.projectName))).sort((a, b) => a.localeCompare(b));
  }, [summaryRows, entitySel, userSel]);

  const categoryOptions = useMemo(() => {
    let base = summaryRows;
    base = applySel(base, (r) => r.entity, entitySel);
    base = applySel(base, (r) => r.user, userSel);
    base = applySel(base, (r) => r.projectName, projectSel);
    return Array.from(
      new Set(base.map((r) => r.category).filter((c) => c && c !== "—"))
    ).sort((a, b) => a.localeCompare(b));
  }, [summaryRows, entitySel, userSel, projectSel]);

  useEffect(() => {
    if (!categorySel.length) return;
    const allowed = new Set(categoryOptions);
    const next = categorySel.filter((v) => allowed.has(v));
    if (next.length !== categorySel.length) setCategorySel(next);
  }, [categoryOptions, categorySel]);

  const stageOptions = useMemo(() => {
    let base = summaryRows;
    if (entitySel.length) base = base.filter((r) => entitySel.includes(r.entity));
    if (projectSel.length) base = base.filter((r) => projectSel.includes(r.projectName));
    const s = new Set(base.map((r) => r.stage).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [summaryRows, entitySel, projectSel]);

  useEffect(() => {
    if (!stageSel.length) return;
    const allowed = new Set(stageOptions);
    const next = stageSel.filter((v) => allowed.has(v));
    if (next.length !== stageSel.length) setStageSel(next);
  }, [stageOptions, stageSel]);

  // boot once from URL (entity/project)
  const [booted, setBooted] = useState(false);
  useEffect(() => {
    if (booted || !summaryRows.length) return;

    const normKey = (s) => (s ?? "").toString().trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    const pick = (val, opts) => {
      if (!val) return null;
      const needle = normKey(val);
      return opts.find((o) => normKey(o) === needle) || null;
    };

    const e = pick(searchParams.get("entity"), entityOptions);
    const p = pick(searchParams.get("project"), projectOptions);

    if (e) setEntitySel([e]);
    if (p) setProjectSel([p]);
    setBooted(true);
  }, [booted, searchParams, summaryRows.length, entityOptions, projectOptions]);

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const clearFilters = () => {
    setEntitySel([]); setUserSel([]); setProjectSel([]); setCategorySel([]);
    setEntityQuery(""); setProjectQuery(""); setCategoryQuery("");
    setStageSel([]); setStageQuery("");
    setDueFrom(""); setDueTo("");
    setSelectedUser(null); setFilterStatus(""); setFilterCategories([]);
    setReviewedFilter("All"); // ✅ NEW reset
    setPage(1);
  };

  const filtered = useMemo(() => {
    return summaryRows.filter((r) => {
      const entOk = !entitySel.length || entitySel.includes(r.entity);
      const projOk = !projectSel.length || projectSel.includes(r.projectName);
      const catOk  = !categorySel.length || categorySel.includes(r.category);
      const stageOk = !stageSel.length || stageSel.includes(r.stage);

      const d = parseDMY(r.dueDate);
      const fromOk = !dueFrom || (d && d >= new Date(dueFrom));
      const toOk   = !dueTo   || (d && d <= new Date(`${dueTo}T23:59:59`));

      return entOk && projOk && catOk && stageOk && fromOk && toOk;
    });
  }, [summaryRows, entitySel, projectSel, categorySel, stageSel, dueFrom, dueTo]);

  /* -------- sort & page -------- */
  const [sortField, setSortField] = useState("stage");
  const [sortOrder, setSortOrder] = useState("desc");
  const onSort = (field) => {
    setSortOrder((prev) => (sortField === field ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortField(field);
  };

  const sorted = useMemo(() => {
    const toNum = (v) => (typeof v === "number" ? v : Number(v) || 0);
    const toStr = (v) => (v ?? "").toString().toLowerCase();
    const toMs = (ddmmyyyy) => {
      const [dd, mm, yyyy] = String(ddmmyyyy || "").split("/");
      const d = dd && mm && yyyy ? new Date(+yyyy, +mm - 1, +dd) : null;
      return d ? d.getTime() : 0;
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      const A =
        sortField === "dueDate" ? toMs(a.dueDate) :
        sortField === "totalTasks" ? toNum(a.totalTasks) :
        sortField === "Pending" ? toNum(a.Pending) :
        sortField === "In Progress" ? toNum(a["In Progress"]) :
        sortField === "Completed" ? toNum(a.Completed) :
        sortField === "NA" ? toNum(a["Not Applicable"]) :
        sortField === "Reviewed" ? toNum(a.Reviewed) :
        sortField === "completion" ? toNum(a.completion) :
        sortField === "entity" ? toStr(a.entity) :
        sortField === "projectName" ? toStr(a.projectName) :
        toStr(a.stage);

      const B =
        sortField === "dueDate" ? toMs(b.dueDate) :
        sortField === "totalTasks" ? toNum(b.totalTasks) :
        sortField === "Pending" ? toNum(b.Pending) :
        sortField === "In Progress" ? toNum(b["In Progress"]) :
        sortField === "Completed" ? toNum(b.Completed) :
        sortField === "NA" ? toNum(b["Not Applicable"]) :
        sortField === "Reviewed" ? toNum(b.Reviewed) :
        sortField === "completion" ? toNum(b.completion) :
        sortField === "entity" ? toStr(b.entity) :
        sortField === "projectName" ? toStr(b.projectName) :
        toStr(b.stage);

      if (A < B) return sortOrder === "asc" ? -1 : 1;
      if (A > B) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortField, sortOrder]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(sorted.length, startIndex + pageSize);
  const pageRows = useMemo(() => sorted.slice(startIndex, endIndex), [sorted, startIndex, endIndex]);

  const openTasksForRow = (row) => {
    const params = new URLSearchParams();
    if (row.entity) params.set("entity", row.entity);
    if (row.user) params.set("user", row.user);
    if (row.projectName) params.set("project", row.projectName);
    navigate(`/tasks?${params.toString()}`);
  };

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>User & Task Summary</Typography>

      {loading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Admin/Manager-only meta filters */}
      {isAdminOrManager && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <Autocomplete
            size="small"
            sx={{ width: 260 }}
            options={usersForFilterScoped}
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            getOptionLabel={(o) => o?.name ?? ""}
            isOptionEqualToValue={(opt, val) =>
              opt?.id && val?.id
                ? opt.id === val.id
                : (opt?.name || "").trim().toLowerCase() === (val?.name || "").trim().toLowerCase()
            }
            renderInput={(params) => (
              <TextField {...params} label="Filter by user" placeholder="All users" />
            )}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={filterStatus || "All"}
            onChange={(_, v) => setFilterStatus(v === "All" ? "" : v)}
          >
            <ToggleButton value="All">All</ToggleButton>
            <ToggleButton value="Active">Active</ToggleButton>
            <ToggleButton value="Inactive">Inactive</ToggleButton>
          </ToggleButtonGroup>

          <Autocomplete
            multiple
            size="small"
            sx={{ width: 300 }}
            options={categoryOptionsScoped}
            value={filterCategories}
            onChange={(_, v) => setFilterCategories(v)}
            renderInput={(params) => (
              <TextField {...params} label="User Category" placeholder="Search user categories…" />
            )}
          />

          {selectedUser && (
            <Button size="small" onClick={() => setSelectedUser(null)}>Clear user</Button>
          )}
        </Box>
      )}

      {/* Existing table filters + NEW Reviewed filter */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
        <TextField
          select size="small" label="Entity" value={entitySel}
          onChange={(e) => { setEntitySel(Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(",")); setPage(1); }}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.length ? `${selected.length} selected` : "All",
            MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
          }}
          sx={{ minWidth: 200 }}
        >
          <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
            <TextField
              autoFocus size="small" placeholder="Search entities…"
              value={entityQuery}
              onChange={(e) => setEntityQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              fullWidth
            />
          </ListSubheader>
          {entityOptions
            .filter((v) => v.toLowerCase().includes(entityQuery.toLowerCase()))
            .map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox size="small" checked={entitySel.indexOf(name) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
        </TextField>

        <TextField
          select size="small" label="Project" value={projectSel}
          onChange={(e) => { setProjectSel(Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(",")); setPage(1); }}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.length ? `${selected.length} selected` : "All",
            MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
          }}
          sx={{ minWidth: 220 }}
        >
          <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
            <TextField
              size="small" placeholder="Search projects…"
              value={projectQuery}
              onChange={(e) => setProjectQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              fullWidth
            />
          </ListSubheader>
          {projectOptions
            .filter((v) => v.toLowerCase().includes(projectQuery.toLowerCase()))
            .map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox size="small" checked={projectSel.indexOf(name) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
        </TextField>

        <TextField
          select size="small" label="Project Category" value={categorySel}
          onChange={(e) => { const v = e.target.value; setCategorySel(Array.isArray(v) ? v : String(v).split(",")); setPage(1); }}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.length ? `${selected.length} selected` : "All",
            MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
          }}
          sx={{ minWidth: 220 }}
        >
          <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
            <TextField
              size="small" placeholder="Search category…"
              value={categoryQuery}
              onChange={(e) => setCategoryQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              fullWidth
            />
          </ListSubheader>
          {categoryOptions
            .filter((v) => v.toLowerCase().includes(categoryQuery.toLowerCase()))
            .map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox size="small" checked={categorySel.indexOf(name) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
        </TextField>

        <TextField
          label="Due from"
          type="date"
          size="small"
          value={dueFrom}
          onChange={(e) => { setDueFrom(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 130 }}
        />
        <TextField
          label="Due to"
          type="date"
          size="small"
          value={dueTo}
          onChange={(e) => { setDueTo(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 130 }}
        />

        <TextField
          select
          size="small"
          label="Task Stage"
          value={stageSel}
          onChange={(e) => { const v = e.target.value; setStageSel(Array.isArray(v) ? v : String(v).split(",")); setPage(1); }}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.length ? `${selected.length} selected` : "All",
            MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
          }}
          sx={{ minWidth: 150 }}
        >
          <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
            <TextField
              size="small"
              placeholder="Search stage…"
              value={stageQuery}
              onChange={(e) => setStageQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              fullWidth
            />
          </ListSubheader>
          {stageOptions
            .filter((v) => v.toLowerCase().includes(stageQuery.toLowerCase()))
            .map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox size="small" checked={stageSel.indexOf(name) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
        </TextField>

        {/* ✅ NEW: Reviewed filter control */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Reviewed:
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={reviewedFilter}
            onChange={(_, v) => { if (v) { setReviewedFilter(v); setPage(1); } }}
          >
            <ToggleButton value="All">All</ToggleButton>
            <ToggleButton value="Reviewed">Reviewed</ToggleButton>
            <ToggleButton value="Not Reviewed">Not reviewed</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ flex: 1 }} />

        <FormControl size="small" sx={{ minWidth: 70 }}>
          <TextField
            select
            size="small"
            label="Show"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {[10, 25, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
        </FormControl>

        <Button variant="outlined" onClick={clearFilters}>Clear Filters</Button>
      </Box>

      <DataTable
        rows={pageRows}
        columns={[
          "entity", "user", "projectName", "dueDate",
          "totalTasks", "Pending", "In Progress", "Completed", "NA",
          "Reviewed",
          "completion", "stage", "tasks"
        ]}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder}
        startIndex={(page - 1) * pageSize}
        onOpenTasks={openTasksForRow}
      />

      <Box mt={2} display="flex" justifyContent="flex-end">
        <Pagination
          count={pageCount}
          page={page}
          onChange={(_, v) => setPage(v)}
          color="primary"
          shape="rounded"
          size="small"
        />
      </Box>
    </Box>
  );
}
